import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { useTheme } from '../contexts/ThemeContext';
import { 
  ArrowRight, 
  Shield, 
  Target, 
  Users, 
  Compass,
  Sparkles,
  ChevronRight,
  Moon,
  Sun
} from 'lucide-react';

const About = () => {
  const { isDark, toggleTheme } = useTheme();
  
  const founderImageUrl = "https://customer-assets.emergentagent.com/job_7828806c-8bc7-44e8-9c95-9c2552be4ec5/artifacts/gdgls01x_pro%20image.png";

  return (
    <div className={`min-h-screen ${isDark ? 'dark bg-navy-900' : 'bg-white'}`} data-testid="about-page">
      {/* Navigation Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-white/95 dark:bg-navy-900/95 backdrop-blur-sm border-b border-navy-100 dark:border-navy-800">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            <div className="w-10 h-10 bg-gradient-to-br from-amber-500 to-amber-600 rounded-xl flex items-center justify-center">
              <Shield className="w-6 h-6 text-white" />
            </div>
            <div>
              <span className="font-heading text-xl font-bold text-navy-900 dark:text-white">Atlas</span>
              <span className="hidden sm:inline text-navy-500 dark:text-navy-400 text-sm ml-1">Career Solutions</span>
            </div>
          </Link>
          <div className="flex items-center gap-4">
            <button
              onClick={toggleTheme}
              className="p-2 rounded-lg text-navy-500 hover:bg-navy-100 dark:text-navy-400 dark:hover:bg-navy-800 transition-colors"
              aria-label="Toggle theme"
            >
              {isDark ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
            </button>
            <Link to="/login">
              <Button variant="outline" className="dark:border-navy-600 dark:text-white dark:hover:bg-navy-800">
                Sign In
              </Button>
            </Link>
            <Link to="/register">
              <Button className="bg-amber-600 hover:bg-amber-700">
                Get Started
              </Button>
            </Link>
          </div>
        </div>
      </header>

      {/* SECTION 1 — Hero / Introduction */}
      <section className="pt-32 pb-20 px-6 bg-gradient-to-b from-navy-50 to-white dark:from-navy-800 dark:to-navy-900">
        <div className="max-w-4xl mx-auto text-center">
          <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold text-navy-900 dark:text-white mb-6 leading-tight">
            About Atlas Career Solutions
          </h1>
          <p className="text-xl sm:text-2xl text-navy-600 dark:text-navy-300 max-w-3xl mx-auto leading-relaxed">
            Built to help professionals grow, transition, and lead with confidence in a changing world.
          </p>
        </div>
      </section>

      {/* SECTION 2 — About Atlas (Mission & Purpose) */}
      <section className="py-20 px-6 bg-white dark:bg-navy-900">
        <div className="max-w-4xl mx-auto">
          <div className="flex items-center gap-3 mb-8">
            <div className="w-12 h-12 bg-amber-100 dark:bg-amber-900/50 rounded-xl flex items-center justify-center">
              <Target className="w-6 h-6 text-amber-600 dark:text-amber-400" />
            </div>
            <h2 className="font-heading text-3xl font-bold text-navy-900 dark:text-white">
              Atlas Career Solutions — Built for People Ready to Move Forward
            </h2>
          </div>

          <div className="prose prose-lg max-w-none dark:prose-invert">
            <p className="text-navy-700 dark:text-navy-200 text-lg leading-relaxed mb-6">
              Atlas Career Solutions exists for one reason: <strong className="text-navy-900 dark:text-white">to help people take control of their professional future with clarity, confidence, and strategy.</strong>
            </p>

            <p className="text-navy-600 dark:text-navy-300 leading-relaxed mb-6">
              The modern job market moves fast. Technology evolves, industries shift, and traditional career advice often fails to keep pace. Atlas was created to bridge that gap — combining practical coaching, modern tools, and AI-powered guidance into one unified ecosystem designed for real-world success.
            </p>

            <p className="text-navy-600 dark:text-navy-300 leading-relaxed mb-8">
              At Atlas, we believe career growth isn't just about landing a job. It's about building a life aligned with purpose, financial stability, and long-term opportunity.
            </p>

            <div className="bg-navy-50 dark:bg-navy-800 rounded-2xl p-8 mb-8">
              <p className="text-navy-700 dark:text-navy-200 font-medium mb-4">Our platform is built to help professionals:</p>
              <ul className="space-y-3">
                {[
                  'Navigate career transitions with confidence',
                  'Translate their experience into marketable value',
                  'Build stronger resumes, personal brands, and networking strategies',
                  'Leverage modern technology — including AI — to stay competitive',
                  'Develop the mindset and strategy needed for sustainable success'
                ].map((item, idx) => (
                  <li key={idx} className="flex items-start gap-3 text-navy-600 dark:text-navy-300">
                    <ChevronRight className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>

            <p className="text-navy-700 dark:text-navy-200 text-lg leading-relaxed mb-6">
              <strong className="text-navy-900 dark:text-white">But Atlas is more than tools and resources.</strong>
            </p>

            <p className="text-navy-600 dark:text-navy-300 leading-relaxed mb-6">
              It's a community built around growth, accountability, and shared ambition — a place where professionals can learn, connect, and move forward together.
            </p>

            <p className="text-navy-600 dark:text-navy-300 leading-relaxed mb-8">
              Whether you're a veteran transitioning into civilian life, a professional seeking your next breakthrough, or someone ready to redefine your trajectory, Atlas is designed to meet you where you are — and help you get where you want to go.
            </p>

            <div className="border-l-4 border-amber-500 pl-6 py-2">
              <p className="text-xl text-navy-900 dark:text-white font-medium italic">
                This is not just career coaching.<br />
                <span className="text-amber-600 dark:text-amber-400">This is career evolution.</span>
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Divider */}
      <div className="bg-white dark:bg-navy-900 px-6">
        <div className="max-w-4xl mx-auto">
          <div className="h-px bg-gradient-to-r from-transparent via-navy-200 dark:via-navy-700 to-transparent"></div>
        </div>
      </div>

      {/* SECTION 3 & 4 — Founder Spotlight */}
      <section className="py-20 px-6 bg-white dark:bg-navy-900">
        <div className="max-w-6xl mx-auto">
          <div className="flex items-center gap-3 mb-12 justify-center">
            <div className="w-12 h-12 bg-amber-100 dark:bg-amber-900/50 rounded-xl flex items-center justify-center">
              <Users className="w-6 h-6 text-amber-600 dark:text-amber-400" />
            </div>
            <h2 className="font-heading text-3xl font-bold text-navy-900 dark:text-white">
              Meet the Founder
            </h2>
          </div>

          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-start">
            {/* Founder Photo */}
            <div className="relative">
              <div className="aspect-[4/5] bg-gradient-to-br from-navy-100 to-navy-200 dark:from-navy-700 dark:to-navy-800 rounded-2xl overflow-hidden shadow-2xl">
                <img 
                  src={founderImageUrl}
                  alt="Richard Dantzler - Founder of Atlas Career Solutions"
                  className="w-full h-full object-cover object-top"
                  data-testid="founder-image"
                />
              </div>
              {/* Decorative elements */}
              <div className="absolute -bottom-4 -right-4 w-32 h-32 bg-amber-500/10 rounded-2xl -z-10"></div>
              <div className="absolute -top-4 -left-4 w-24 h-24 bg-navy-500/10 dark:bg-navy-400/10 rounded-2xl -z-10"></div>
            </div>

            {/* Founder Text */}
            <div className="prose prose-lg max-w-none dark:prose-invert">
              <h3 className="font-heading text-2xl font-bold text-navy-900 dark:text-white mb-6">
                Richard Dantzler
              </h3>
              
              <p className="text-navy-700 dark:text-navy-200 text-lg leading-relaxed mb-6">
                <strong className="text-navy-900 dark:text-white">Atlas Career Solutions was built from lived experience — not theory.</strong>
              </p>

              <p className="text-navy-600 dark:text-navy-300 leading-relaxed mb-6">
                Richard Dantzler is a veteran, intelligence professional, entrepreneur, and strategist whose career has been defined by leadership, service, and transformation. After spending years operating in high-stakes environments — from military intelligence operations to organizational security and risk analysis — Richard witnessed firsthand how talented individuals often struggle to translate their experience into meaningful career advancement.
              </p>

              <p className="text-navy-700 dark:text-navy-200 font-semibold mb-4">He saw a gap.</p>

              <p className="text-navy-600 dark:text-navy-300 leading-relaxed mb-6">
                Too many professionals — especially veterans and transitioning leaders — possessed extraordinary skills but lacked the tools, guidance, and modern strategies needed to navigate today's evolving workforce.
              </p>

              <p className="text-amber-600 dark:text-amber-400 font-semibold text-lg mb-6">
                Atlas was created to change that.
              </p>

              <p className="text-navy-600 dark:text-navy-300 leading-relaxed mb-6">
                Driven by a deep passion for growth, mentorship, and community impact, Richard built Atlas Career Solutions to serve as more than a coaching platform. His vision was to create an ecosystem that combines strategic coaching, technology, and practical education to help people unlock their full potential.
              </p>

              <p className="text-navy-600 dark:text-navy-300 leading-relaxed mb-6">
                His leadership philosophy is rooted in service — empowering others to build confidence, develop clarity, and take ownership of their future.
              </p>

              <p className="text-navy-600 dark:text-navy-300 leading-relaxed mb-6">
                Beyond Atlas, Richard is a serial entrepreneur focused on real estate, technology, and community-focused ventures designed to create long-term impact. His work reflects a belief that success is most meaningful when it creates opportunity for others.
              </p>

              <div className="bg-navy-50 dark:bg-navy-800 rounded-xl p-6 mb-6">
                <p className="text-navy-700 dark:text-navy-200 font-medium">
                  Today, Atlas represents the intersection of his experience, values, and mission:
                </p>
                <p className="text-amber-600 dark:text-amber-400 font-semibold mt-2">
                  Helping people move from uncertainty to direction — and from potential to execution.
                </p>
              </div>

              <div className="border-l-4 border-amber-500 pl-6 py-2">
                <p className="text-xl text-navy-900 dark:text-white font-medium italic">
                  Atlas isn't just a business.<br />
                  <span className="text-amber-600 dark:text-amber-400">It's a commitment to helping others rise.</span>
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* SECTION 5 — Call to Action */}
      <section className="py-24 px-6 bg-gradient-to-br from-navy-900 to-navy-800">
        <div className="max-w-4xl mx-auto text-center">
          <div className="w-16 h-16 bg-amber-500 rounded-2xl flex items-center justify-center mx-auto mb-8">
            <Compass className="w-8 h-8 text-white" />
          </div>
          <h2 className="font-heading text-3xl sm:text-4xl font-bold text-white mb-6">
            Ready to Build Your Next Chapter?
          </h2>
          <p className="text-navy-300 text-lg mb-10 max-w-2xl mx-auto">
            Join thousands of professionals who are taking control of their career journey with Atlas.
          </p>
          <Link to="/membership">
            <Button 
              size="lg" 
              className="bg-amber-600 hover:bg-amber-700 text-lg px-8 py-6 h-auto"
              data-testid="cta-join-membership"
            >
              <Sparkles className="w-5 h-5 mr-2" />
              Join Atlas Membership
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </Link>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 px-6 bg-navy-950 border-t border-navy-800">
        <div className="max-w-7xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <Shield className="w-5 h-5 text-amber-500" />
            <span className="text-navy-400 text-sm">
              © {new Date().getFullYear()} Atlas Career Solutions. All rights reserved.
            </span>
          </div>
          <div className="flex items-center gap-6">
            <Link to="/" className="text-navy-400 hover:text-white text-sm transition-colors">
              Home
            </Link>
            <Link to="/testimonials" className="text-navy-400 hover:text-white text-sm transition-colors">
              Testimonials
            </Link>
            <Link to="/login" className="text-navy-400 hover:text-white text-sm transition-colors">
              Sign In
            </Link>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default About;
